import { useEffect, useRef } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { trpc } from "@/trpc/client";
import {
  consumeLinkedinIntegrationOAuthStateWithMode,
  linkedinIntegrationRedirectUri,
} from "@/lib/linkedin-integration-oauth";

/**
 * LinkedIn's redirect back after the posting-app OAuth consent screen
 * (#681), distinct from the sign-in flow's `/linkedin-callback`
 * (`_home.linkedin-callback.tsx`) since this connects an already
 * authenticated workspace rather than signing a user in.
 *
 * The URL carries no `workspaceId`: LinkedIn matches `redirect_uri` exactly
 * against the app's registered list, so there is one callback URL for every
 * workspace and the workspace is recovered from the OAuth `state` instead
 * (see `lib/linkedin-integration-oauth.ts`). It still lives
 * under `/dashboard`, so the authenticated-subtree guard in `dashboard.tsx`
 * applies; the `_` suffix keeps it out of any future `/dashboard/integrations`
 * page's subtree while serving that URL prefix.
 */
export const Route = createFileRoute("/dashboard/integrations_/linkedin-callback")({
  component: LinkedinIntegrationCallbackPage,
});

function LinkedinIntegrationCallbackPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const processedRef = useRef(false);

  // The workspace is resolved from the OAuth `state` inside the effect, so
  // the mutation's callbacks read it back off `variables` rather than closing
  // over a route param that this URL no longer carries.
  const connectMutation = useMutation({
    ...trpc.authenticatedRouterFunction.connectLinkedinIntegration.mutationOptions(),
    onSuccess: (result, variables) => {
      void queryClient.invalidateQueries({
        queryKey: trpc.authenticatedRouterFunction.getIntegrations.queryOptions({
          workspaceId: variables.workspaceId,
        }).queryKey,
      });
      // A reconnect resets the author to the profile and replaces the page list.
      void queryClient.invalidateQueries({
        queryKey: trpc.authenticatedRouterFunction.getLinkedinAuthorOptions.queryOptions({
          workspaceId: variables.workspaceId,
        }).queryKey,
      });
      toast.success("LinkedIn connected", { description: result.account });
      // Own-app connect only (#1053/US1): the member IS connected, only the
      // page list is missing — a warning after the success toast, not an
      // error in place of it.
      if (result.organizationsError) {
        toast.warning(result.organizationsError);
      }
    },
    onError: (err) => {
      toast.error("Could not connect LinkedIn", {
        description: err.message ?? "Unknown error occurred.",
      });
    },
    onSettled: (_data, _err, variables) => {
      void navigate({
        to: "/dashboard/w/$workspaceId/integrations",
        params: { workspaceId: variables.workspaceId },
      });
    },
  });

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;

    const params = new URLSearchParams(window.location.search);
    // Consumed first, and unconditionally: the state is single-use whatever
    // the outcome, and its workspace is also where an error should land the
    // author back.
    const consumed = consumeLinkedinIntegrationOAuthStateWithMode(params.get("state"));
    const returnedWorkspaceId = consumed?.workspaceId ?? null;

    const goBack = () => {
      void (returnedWorkspaceId
        ? navigate({
            to: "/dashboard/w/$workspaceId/integrations",
            params: { workspaceId: returnedWorkspaceId },
          })
        : // No state means no workspace to return to; `/dashboard` resolves
          // the user's first workspace.
          navigate({ to: "/dashboard" }));
    };

    const error = params.get("error");
    if (error) {
      toast.error(
        params.get("error_description") ||
          "LinkedIn connection did not complete. Please try again.",
      );
      goBack();
      return;
    }

    const code = params.get("code");
    if (!code || !consumed) {
      toast.error("Invalid LinkedIn callback. Please try again.");
      goBack();
      return;
    }

    connectMutation.mutate({
      workspaceId: consumed.workspaceId,
      code,
      redirectUri: linkedinIntegrationRedirectUri(),
      mode: consumed.mode,
      // Own-app only: which app this attempt's consent was for, so the server
      // can refuse a callback whose pending app pair has since been replaced.
      ...(consumed.clientId ? { clientId: consumed.clientId } : {}),
    });
    // One-shot on mount: `consumeLinkedinIntegrationOAuthStateWithMode` deletes the
    // single-use state, so a second run could only ever fail validation.
    // `processedRef` is the real guard — naming the deps would just re-fire on
    // `connectMutation`'s per-render identity and be short-circuited by it.
    // Same shape as the sign-in callback (`_home/linkedin-callback.tsx`).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex flex-1 items-center justify-center">
      <div className="text-sm text-muted-foreground">Connecting LinkedIn...</div>
    </div>
  );
}
